import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";

import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { api, type ChatDetail, type ChatLiveSnapshot, type ChatMessageRow } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { syncNavPath } from "@/hooks/useNavPerf";
import { mark, startTimer, type PerfTimer } from "@/lib/perf";
import { captureError } from "@/lib/logger";
import { normalizeEvent, streamChat, streamChatLive, streamProjectChat, type StreamDispatchEvent } from "@/lib/stream";
import type { AssistantChatMessage, ChatInputPayload } from "@/components/assistant/types";
import { isEditAnnotation, rowToAssistantMessage } from "@/components/assistant/types";
import { assistantMessage, finalizeAssistantMessages, reduceAssistantMessages, uid } from "@/hooks/assistantChat/messageReducer";
import { assistantMessageToRow, toHistory } from "@/hooks/assistantChat/messageRows";

const RECONNECTING_BANNER_DELAY_MS = 4000;

export interface UseAssistantChatOptions {
  chatId?: string | null;
  projectId?: string;
  initialRows?: ChatMessageRow[];
  /** In-flight turn from the GET /chat/{id} snapshot; triggers hydrate + live tail on mount. */
  initialLive?: ChatLiveSnapshot;
  onChatId?: (chatId: string) => void;
  /** When true, URL adoption uses history.replaceState (no remount). */
  sameSurface?: boolean;
}

export function useAssistantChat(options: UseAssistantChatOptions = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { saveChat, setCurrentChatId } = useChatHistory();
  const [chatId, setChatId] = useState<string | null>(options.chatId ?? null);
  const [messages, setMessages] = useState<AssistantChatMessage[]>(() => (options.initialRows ? options.initialRows.map(rowToAssistantMessage) : []));
  const [isResponseLoading, setIsResponseLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const titleGeneratedRef = useRef(false);
  const firstUserTextRef = useRef<string | null>(null);
  const chatIdRef = useRef<string | null>(options.chatId ?? null);
  const finalizedRef = useRef<AssistantChatMessage[]>([]);
  const stopTotalRef = useRef<PerfTimer | null>(null);
  const submitTsRef = useRef<number>(0);
  const firstTokenEmittedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectingRawRef = useRef(false);

  // Resumable-streaming bookkeeping: the in-flight turn id (for cancel), the resume cursor
  // (latest chat_events id), the set of applied event ids (dedupe), the chat whose live tail
  // we currently own, and the chat we have already hydrated from a snapshot.
  const turnIdRef = useRef<string | null>(null);
  const cursorRef = useRef<string | null>(null);
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const liveSubscribedRef = useRef<string | null>(null);
  const hydratedChatRef = useRef<string | null>(null);

  const applyReconnecting = useCallback((state: boolean) => {
    if (state === reconnectingRawRef.current) return;
    reconnectingRawRef.current = state;
    if (state) {
      // Debounce: only surface the banner if the drop persists — hides sub-second resumes.
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        setReconnecting(true);
      }, RECONNECTING_BANNER_DELAY_MS);
    } else {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setReconnecting(false);
    }
  }, []);

  useEffect(() => {
    setChatId(options.chatId ?? null);
    chatIdRef.current = options.chatId ?? null;
  }, [options.chatId]);

  useEffect(() => {
    // Skip resetting committed rows while a live subscription owns the message list, so an
    // in-flight turn folded from the snapshot/tail is not clobbered by a background refetch.
    if (options.initialRows && liveSubscribedRef.current === null) {
      setMessages(options.initialRows.map(rowToAssistantMessage));
    }
  }, [options.initialRows]);

  useEffect(
    // Detach the live subscription on unmount; the durable event log lets a later mount replay
    // the snapshot and resume the tail. The detached driver keeps generating after the socket
    // drops, so the turn finalizes regardless of when the client reconnects.
    () => () => {
      abortRef.current?.abort();
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    },
    []
  );

  const finalizeAssistant = () => {
    setMessages((current) => {
      const finalized = finalizeAssistantMessages(current);
      finalizedRef.current = finalized;
      return finalized;
    });
    setIsResponseLoading(false);
    applyReconnecting(false);
  };

  const generateTitle = async (resolvedChatId: string) => {
    if (titleGeneratedRef.current || !firstUserTextRef.current) {
      return;
    }

    titleGeneratedRef.current = true;
    try {
      const result = await api.chat.generateTitle(resolvedChatId, firstUserTextRef.current);
      saveChat({ id: resolvedChatId, title: result.title, project_id: options.projectId ?? null });
      queryClient.setQueryData<InfiniteData<ChatDetail, string | null>>(qk.chat(resolvedChatId), (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page, index) => (index === 0 ? { ...page, chat: { ...page.chat, title: result.title } } : page))
            }
          : old
      );
    } catch {
      // Title generation is non-blocking; the chat itself has already completed.
    }
  };

  const invalidateProjectLists = () => {
    if (options.projectId) {
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === qk.projects[0] && queryKey[1] === qk.projects[1],
      });
    }
  };

  const adoptChatId = (nextChatId: string) => {
    setChatId(nextChatId);
    chatIdRef.current = nextChatId;
    options.onChatId?.(nextChatId);
    setCurrentChatId(nextChatId);
    saveChat({ id: nextChatId, title: firstUserTextRef.current?.slice(0, 120) ?? "New chat", project_id: options.projectId ?? null, created_at: new Date().toISOString() });
    invalidateProjectLists();
    const path = options.projectId ? `/projects/${options.projectId}/assistant/chat/${nextChatId}` : `/assistant/chat/${nextChatId}`;
    if (options.sameSurface) {
      // Bypass TanStack Router's monkey-patched replaceState to avoid
      // triggering a route re-evaluation that would unmount this component
      // and lose the in-flight streaming state.
      History.prototype.replaceState.call(window.history, window.history.state, "", path);
      syncNavPath(path);
    } else {
      void navigate({ to: path, replace: true });
    }
  };

  // Side effects live here (navigation, perf marks, error banner, turn id); the
  // message-state machine itself is the pure reducer in assistantChat/messageReducer.
  const applyStreamEvent = (event: StreamDispatchEvent) => {
    switch (event.type) {
      case "error":
      case "turn_failed":
        setError(event.message);
        setMessages((current) => reduceAssistantMessages(current, event));
        return;
      case "chat_id":
        adoptChatId(event.chatId || event.chat_id);
        return;
      case "turn_meta":
        // Transport-only: identifies the in-flight turn so Stop can cancel it. Not rendered.
        turnIdRef.current = event.turn_id;
        return;
      case "content_delta":
        if (!firstTokenEmittedRef.current) {
          firstTokenEmittedRef.current = true;
          mark("chat.first_token", { duration_ms: Math.round(performance.now() - submitTsRef.current) });
        }
        break;
      case "content_done":
        mark("chat.content_done");
        break;
      default:
        break;
    }
    setMessages((current) => reduceAssistantMessages(current, event));
  };

  const foldStreamEvent = (event: StreamDispatchEvent, eventId?: string) => {
    // Dedupe by chat_events id so replayed snapshot/tail overlap is idempotent, and advance
    // the resume cursor. Transport-only events without an id (e.g. chat_id) pass straight through.
    if (eventId) {
      if (seenEventIdsRef.current.has(eventId)) {
        return;
      }
      seenEventIdsRef.current.add(eventId);
      cursorRef.current = eventId;
    }
    applyStreamEvent(event);
  };

  const applyStreamEventRef = useRef(applyStreamEvent);
  applyStreamEventRef.current = applyStreamEvent;
  const foldStreamEventRef = useRef(foldStreamEvent);
  foldStreamEventRef.current = foldStreamEvent;
  const finalizeAssistantRef = useRef(finalizeAssistant);
  finalizeAssistantRef.current = finalizeAssistant;
  const initialLiveRef = useRef(options.initialLive);
  initialLiveRef.current = options.initialLive;
  const hasInitialLive = !!options.initialLive;

  useEffect(() => {
    // Hydrate an in-flight turn from the GET /chat/{id} snapshot, then open the resumable live
    // tail from the snapshot cursor. Runs once per chat that arrives mid-turn (reload, navigation,
    // second tab). Completed turns arrive via initialRows and skip this path.
    const live = initialLiveRef.current;
    const cid = options.chatId ?? null;
    if (!live || !cid || hydratedChatRef.current === cid) {
      return;
    }
    hydratedChatRef.current = cid;
    chatIdRef.current = cid;
    turnIdRef.current = live.turn_id;
    cursorRef.current = live.cursor ?? null;
    seenEventIdsRef.current = new Set();

    setMessages((current) => [...current, assistantMessage()]);
    for (const { id, event } of live.events) {
      if (id) {
        seenEventIdsRef.current.add(id);
        cursorRef.current = id;
      }
      const normalized = normalizeEvent(event);
      if (normalized) {
        applyStreamEventRef.current(normalized);
      }
    }

    setIsResponseLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    liveSubscribedRef.current = cid;
    streamChatLive(cid, {
      lastEventId: cursorRef.current,
      onEvent: (event, eventId) => foldStreamEventRef.current(event, eventId),
      onDone: () => {
        liveSubscribedRef.current = null;
        applyReconnecting(false);
        finalizeAssistantRef.current();
      },
      onError: (streamError) => setError(streamError.message),
      onReconnecting: applyReconnecting,
      signal: controller.signal
    });

    return () => {
      controller.abort();
    };
  }, [options.chatId, hasInitialLive, applyReconnecting]);

  const submit = async (payload: ChatInputPayload) => {
      if (isResponseLoading) {
        return;
      }

      setError(null);
      const userFiles = payload.attachedDocuments.map((doc) => ({ filename: doc.filename, document_id: doc.document_id }));
      const userMessage: AssistantChatMessage = {
        id: uid("user"),
        role: "user",
        content: payload.content,
        files: userFiles,
        created_at: new Date().toISOString()
      };
      setMessages((current) => [...current, userMessage, assistantMessage()]);
      setIsResponseLoading(true);
      firstUserTextRef.current ??= payload.content;
      seenEventIdsRef.current = new Set();
      turnIdRef.current = null;
      cursorRef.current = null;
      liveSubscribedRef.current = null;
      // A fresh turn supersedes any snapshot we hydrated from, so allow re-hydration if this
      // chat is later reopened from a new snapshot.
      hydratedChatRef.current = null;

      const controller = new AbortController();
      abortRef.current = controller;

      const stopTotal = startTimer("chat.stream_total");
      stopTotalRef.current = stopTotal;
      submitTsRef.current = performance.now();
      firstTokenEmittedRef.current = false;

      const history = toHistory([...messages, userMessage]);

      // The POST stream now resumes via /live if the socket drops; the detached backend driver keeps the turn alive, and this hook uses the same resume path as the dedicated live-tail subscription.
      const handle = options.projectId
        ? streamProjectChat(
            options.projectId,
            {
              messages: history,
              chat_id: chatId,
              attached_documents: userFiles,
              web_search_enabled: payload.webSearchEnabled,
              workflow_enabled: payload.workflowEnabled
            },
            {
              onEvent: foldStreamEvent,
              onDone: finalizeAssistant,
              onError: (streamError) => setError(streamError.message),
              onReconnecting: applyReconnecting,
              resolveReconnect: () => {
                const cid = chatIdRef.current;
                return cid ? { path: `/chat/${cid}/live`, method: "GET" } : null;
              },
              signal: controller.signal
            }
          )
        : streamChat(
            { messages: history, chat_id: chatId, project_id: null, web_search_enabled: payload.webSearchEnabled },
            {
              onEvent: foldStreamEvent,
              onDone: finalizeAssistant,
              onError: (streamError) => setError(streamError.message),
              onReconnecting: applyReconnecting,
              resolveReconnect: () => {
                const cid = chatIdRef.current;
                return cid ? { path: `/chat/${cid}/live`, method: "GET" } : null;
              },
              signal: controller.signal
            }
          );

      try {
        await handle.done;
        invalidateProjectLists();
        stopTotalRef.current?.stop();
        stopTotalRef.current = null;
        const resolvedChatId = chatIdRef.current ?? undefined;
        if (resolvedChatId) {
          queryClient.setQueryData<InfiniteData<ChatDetail, string | null>>(qk.chat(resolvedChatId), {
            pages: [{
              chat: { id: resolvedChatId, title: firstUserTextRef.current?.slice(0, 120) ?? "New chat", project_id: options.projectId ?? null, created_at: new Date().toISOString() },
              messages: finalizedRef.current.map(assistantMessageToRow),
              next_cursor: null,
              has_more: false
            }],
            pageParams: [null]
          });
          void generateTitle(resolvedChatId);
        }
      } catch {
        stopTotalRef.current?.stop();
        stopTotalRef.current = null;
        // A drop before chat_id arrives leaves no id to resume via /live, so surface a
        // retryable error instead of a silent empty turn. After chat_id, the stream resumes
        // via /live and the detached driver keeps the turn alive, so no error is shown.
        if (!chatIdRef.current) {
          setError("The connection dropped before the assistant turn started. Please retry.");
        }
        finalizeAssistant();
      }
  };

  const stop = () => {
    // Detach the client subscription and durably cancel the turn. Aborting alone would only
    // disconnect this client; POST /chat/{id}/cancel flips the turn terminal server-side.
    const cid = chatIdRef.current;
    abortRef.current?.abort();
    liveSubscribedRef.current = null;
    // Mirror submit(): a later turn on this chat must be able to re-hydrate from a fresh snapshot
    // (e.g. another tab starts a turn after Stop), so clear the hydration guard.
    hydratedChatRef.current = null;
    if (cid) {
      void api.chat.cancel(cid).catch((cancelError) => captureError(cancelError, { scope: "chat.cancel", chat_id: cid }));
    }
    finalizeAssistant();
  };

  const editStatuses = useMemo(() => {
    const statuses = new Map<string, "pending" | "accepted" | "rejected">();
    messages.forEach((message) => {
      message.annotations?.forEach((annotation) => {
        if (isEditAnnotation(annotation)) {
          statuses.set(annotation.edit_id, annotation.status ?? "pending");
        }
      });
    });
    return statuses;
  }, [messages]);

  return {
    messages,
    setMessages,
    isResponseLoading,
    reconnecting,
    chatId,
    error,
    submit,
    stop,
    editStatuses
  };
}
